import { Link } from "react-router-dom";
import { ArrowRight, Users, Leaf, ShieldCheck, MapPin, Eye, type LucideIcon } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export type InlineLink = {
  label: string;
  href: string;
  /** Placeholder token used in text (defaults to "[link]"). Use unique tokens like "[link1]" for multiple links. */
  token?: string;
  /** External (target=_blank) vs internal SPA <Link>. Auto-detected from href if omitted. */
  external?: boolean;
};

export type FAQItem = {
  question: string;
  intro?: string;
  /** Plain airy bullet list */
  bullets?: string[];
  /** Pillar-style list: each item maps to one of the 5 Wise Compass pillars */
  pillars?: PillarBullet[];
  outro?: string;
  link?: { text: string; href: string } | null;
  /** One or many inline links rendered where their token appears (in intro/outro/bullets). */
  inlineLink?: InlineLink;
  inlineLinks?: InlineLink[];
};

export type PillarKey = "human" | "earth" | "time" | "territory" | "vision";

export type PillarBullet = {
  pillar: PillarKey;
  /** Bold label (e.g. "L'Humain") */
  title: string;
  /** Secondary explanation */
  description: string;
};

const PILLAR_VISUAL: Record<
  PillarKey,
  { icon: LucideIcon; color: string; bg: string }
> = {
  human:     { icon: Users,       color: "text-[#C45B3E]", bg: "bg-[#C45B3E]/10" },
  earth:     { icon: Leaf,        color: "text-[#6B8E7A]", bg: "bg-[#6B8E7A]/10" },
  time:      { icon: ShieldCheck, color: "text-[#2D5F5D]", bg: "bg-[#2D5F5D]/10" },
  territory: { icon: MapPin,      color: "text-[#D4A574]", bg: "bg-[#F1CCAB]/40" },
  vision:    { icon: Eye,         color: "text-[#8E6B4B]", bg: "bg-[#8E6B4B]/15" },
};

const isExternal = (href: string) => /^https?:\/\//i.test(href);

const renderLinkNode = (link: InlineLink, key?: string | number) => {
  const external = link.external ?? isExternal(link.href);
  const className = "text-primary font-medium hover:underline";
  if (external) {
    return (
      <a key={key} href={link.href} target="_blank" rel="noopener noreferrer" className={className}>
        {link.label}
      </a>
    );
  }
  return (
    <Link key={key} to={link.href} className={className}>
      {link.label}
    </Link>
  );
};

const renderWithInlineLinks = (
  text: string,
  inline?: InlineLink,
  inlines?: InlineLink[],
) => {
  const links = [...(inlines ?? []), ...(inline ? [inline] : [])];
  if (links.length === 0) return text;

  const tokens = links.map((l) => l.token ?? "[link]");
  const escaped = tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "g");

  const parts = text.split(re);
  if (parts.length === 1) return text;

  return parts.map((part, i) => {
    const match = links.find((l) => (l.token ?? "[link]") === part);
    if (match) return renderLinkNode(match, i);
    return <span key={i}>{part}</span>;
  });
};

interface FAQAccordionProps {
  items: FAQItem[];
  /** Tailwind classes for the question element (e.g. font-serif sizes) */
  questionClassName?: string;
  /** Render the question as <h2> (FAQ page) or plain <span> (homepage section) */
  questionAs?: "h2" | "span";
}

const FAQAccordion = ({
  items,
  questionClassName = "font-serif text-base md:text-lg text-foreground pr-4",
  questionAs = "span",
}: FAQAccordionProps) => {
  const QuestionTag = questionAs;

  return (
    <Accordion type="single" collapsible className="space-y-4">
      {items.map((item, index) => (
        <AccordionItem
          key={index}
          value={`item-${index}`}
          className="bg-card border border-border rounded-xl px-6 md:px-8 data-[state=open]:shadow-soft transition-shadow"
        >
          <AccordionTrigger className="text-left hover:no-underline py-5">
            <QuestionTag className={questionClassName}>
              {item.question}
            </QuestionTag>
          </AccordionTrigger>
          <AccordionContent className="pb-6 pt-2">
            <div className="space-y-5 text-[15px] leading-[1.7]">
              {item.intro && (
                <p className="text-muted-foreground">
                  {renderWithInlineLinks(item.intro, item.inlineLink, item.inlineLinks)}
                </p>
              )}

              {item.pillars && item.pillars.length > 0 && (
                <ul className="space-y-3">
                  {item.pillars.map((p, i) => {
                    const visual = PILLAR_VISUAL[p.pillar];
                    const Icon = visual.icon;
                    return (
                      <li
                        key={i}
                        className="flex items-start gap-4 rounded-lg border border-border/60 bg-background/40 p-4"
                      >
                        <span
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${visual.bg}`}
                          aria-hidden="true"
                        >
                          <Icon className={`h-5 w-5 ${visual.color}`} />
                        </span>
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground">{p.title}</p>
                          <p className="text-muted-foreground/90 mt-0.5">
                            {p.description}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {item.bullets && item.bullets.length > 0 && (
                <ul className="space-y-2.5">
                  {item.bullets.map((b, i) => {
                    const match = b.match(/^([^:]+?)\s*:\s*(.+)$/);
                    if (match) {
                      const [, label, desc] = match;
                      return (
                        <li key={i} className="flex items-start gap-3">
                          <span
                            className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70"
                            aria-hidden="true"
                          />
                          <span>
                            <span className="font-semibold text-foreground">
                              {renderWithInlineLinks(label, item.inlineLink, item.inlineLinks)}
                            </span>
                            <span className="text-muted-foreground">
                              {" — "}
                              {renderWithInlineLinks(desc, item.inlineLink, item.inlineLinks)}
                            </span>
                          </span>
                        </li>
                      );
                    }
                    return (
                      <li key={i} className="flex items-start gap-3 text-muted-foreground">
                        <span
                          className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70"
                          aria-hidden="true"
                        />
                        <span>{renderWithInlineLinks(b, item.inlineLink, item.inlineLinks)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}

              {item.outro && (() => {
                const links = [
                  ...(item.inlineLinks ?? []),
                  ...(item.inlineLink ? [item.inlineLink] : []),
                ];
                const tokens = links.map((l) => l.token ?? "[link]");
                // Find a token that ends the outro (optionally followed by punctuation)
                const trailingToken = tokens.find((t) =>
                  new RegExp(
                    `${t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*[.!?]?\\s*$`,
                  ).test(item.outro!),
                );

                if (trailingToken) {
                  const trailingLink = links.find(
                    (l) => (l.token ?? "[link]") === trailingToken,
                  )!;
                  const bodyText = item.outro!
                    .replace(
                      new RegExp(
                        `\\s*${trailingToken.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*[.!?]?\\s*$`,
                      ),
                      "",
                    )
                    .trim();
                  // Remaining links (excluding the trailing one) for inline rendering in body
                  const otherLinks = links.filter(
                    (l) => (l.token ?? "[link]") !== trailingToken,
                  );
                  return (
                    <>
                      {bodyText && (
                        <p className="text-muted-foreground">
                          {renderWithInlineLinks(bodyText, undefined, otherLinks)}
                        </p>
                      )}
                      <p className="pt-1">
                        {renderLinkNode(trailingLink)}
                      </p>
                    </>
                  );
                }

                return (
                  <p className="text-muted-foreground">
                    {renderWithInlineLinks(item.outro!, item.inlineLink, item.inlineLinks)}
                  </p>
                );
              })()}

              {item.link && (
                <Link
                  to={item.link.href}
                  className="inline-flex items-center gap-1.5 text-primary font-medium hover:underline"
                >
                  {item.link.text}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export default FAQAccordion;
